import { FunctionComponent, useState } from 'react'
import { Accessibility, AccessibilityType, Daycare } from '@/types/Daycare'
import styles from './AccessibilitiesLists.module.scss'
import { TableCell, TableRow } from '@mui/material'
import moment from 'moment-timezone'
import { Delete } from '@mui/icons-material'
import ValidDeleteAccessibilityDialog from './Dialog/validDeleteAccessibilityDialog.'
import EditIcon from '@mui/icons-material/Edit'
import { useTranslation } from 'react-i18next'
import AccessibilityDialog from './Dialog/accessibilityDialog'

interface AccessibilitiesListsProps {
    daycare: Daycare
    canUpdate: boolean
    accessibility: Accessibility
    accessibilities: Array<Accessibility>
    removeOneAccessibility: (accessibilityId: number) => void
    updateOneAccessibility: (accessibility: Accessibility) => void
}

const AccessibilitiesLists: FunctionComponent<AccessibilitiesListsProps> = ({
    daycare,
    canUpdate,
    accessibility,
    accessibilities,
    removeOneAccessibility,
    updateOneAccessibility
}: AccessibilitiesListsProps) => {
    const { t } = useTranslation()

    const [isDeleteAccessibilityDialogOpen, setIsDeleteAccessibilityDialogOpen] = useState<boolean>(false)
    const [isUpdateAccessibilityDialogOpen, setIsUpdateAccessibilityDialogOpen] = useState<boolean>(false)

    return (
        <>
            <TableRow>
                <TableCell sx={{ width: '40%' }} align={'center'} className={styles.RowText}>
                    {moment(accessibility.start, 'HH:mm').format('HH:mm')}
                </TableCell>
                <TableCell sx={{ width: '10%' }} align={'center'} className={styles.RowText}>
                    {t('to')}
                </TableCell>
                <TableCell sx={{ width: '40%' }} align={'center'} className={styles.RowText}>
                    {moment(accessibility.end, 'HH:mm').format('HH:mm')}
                </TableCell>
                {canUpdate && (
                    <>
                        <TableCell sx={{ width: '5%' }} align={'center'}>
                            <EditIcon
                                className={styles.Icon}
                                onClick={() => setIsUpdateAccessibilityDialogOpen(true)}
                            />
                        </TableCell>
                        <TableCell sx={{ width: '5%' }} align={'center'}>
                            <Delete className={styles.Icon} onClick={() => setIsDeleteAccessibilityDialogOpen(true)} />
                        </TableCell>
                    </>
                )}
            </TableRow>
            <ValidDeleteAccessibilityDialog
                open={isDeleteAccessibilityDialogOpen}
                handleClose={() => setIsDeleteAccessibilityDialogOpen(false)}
                accessibilityId={accessibility?.id || 0}
                removeOneAccessibility={removeOneAccessibility}
            />
            <AccessibilityDialog
                isDialogOpen={isUpdateAccessibilityDialogOpen}
                handleCloseDialog={() => setIsUpdateAccessibilityDialogOpen(false)}
                create={false}
                daycare={daycare}
                accessibility={accessibility}
                accessibilities={accessibilities}
                updateOneAccessibility={updateOneAccessibility}
                accessibilityType={accessibility?.type || AccessibilityType.arrival}
            />
        </>
    )
}

export default AccessibilitiesLists

export function getOpeningForAccessibility(daycare: Daycare, format: 'HH:mm' | 'HH' | 'mm'): string {
    let date: string = '23:00'
    if (daycare.opening.monOpen && moment(daycare.opening.monOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.monOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.tueOpen && moment(daycare.opening.tueOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.tueOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.wedOpen && moment(daycare.opening.wedOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.wedOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.thuOpen && moment(daycare.opening.thuOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.thuOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.friOpen && moment(daycare.opening.friOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.friOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.satOpen && moment(daycare.opening.satOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.satOpen, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.sunOpen && moment(daycare.opening.sunOpen, 'HH:mm').format('HH:mm') < date) {
        date = moment(daycare.opening.sunOpen, 'HH:mm').format('HH:mm')
    }
    return moment(date, 'HH:mm').format(format)
}

export function getClosingForAccessibility(daycare: Daycare, format: 'HH:mm' | 'HH' | 'mm'): string {
    let date: string = '00:00'
    if (daycare.opening.monClose && moment(daycare.opening.monClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.monClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.tueClose && moment(daycare.opening.tueClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.tueClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.wedClose && moment(daycare.opening.wedClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.wedClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.thuClose && moment(daycare.opening.thuClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.thuClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.friClose && moment(daycare.opening.friClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.friClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.satClose && moment(daycare.opening.satClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.satClose, 'HH:mm').format('HH:mm')
    }
    if (daycare.opening.sunClose && moment(daycare.opening.sunClose, 'HH:mm').format('HH:mm') > date) {
        date = moment(daycare.opening.sunClose, 'HH:mm').format('HH:mm')
    }
    return moment(date, 'HH:mm').format(format)
}
